import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./FileFolderPreview.module.css";
import i18n from "../../Services/i18n";
import ImageViewer from "../ImageViewer/ImageViewer";
import { MdDelete, MdCreate, MdInsertDriveFile } from "react-icons/md";
import FileService from "../../Services/fileService";
import { useDeletionConfirmationModal } from "../../Hooks/DeletionConfirmationModal";
import mediaLibIcon from "../../Assets/Icons/medialib_black2.png";
import { useModal } from "../../Context/ModalContext";
import { useToasts } from "../../LayoutComponents/ToastContext";
import FileUpdateForm from "./FileUpdateForm";

const FileFolderPreview = ({
  files,
  viewMode,
  smallIcon,
  onFileSelected,
  refresh,
  canDelete = true,
  canEdit = true,
  hideHeaderMenu = false,
}) => {
  const { addToast } = useToasts();
  const { openModal } = useModal();
  const metaDataFormRef = useRef(null);
  const [openFile, setOpenFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "ascending",
  });

  const confirmDelete = useDeletionConfirmationModal();

  useEffect(() => {
    setSelectedFile(null);
  }, [files]);

  const handleCloseImageViewer = useCallback(() => setOpenFile(null), []);

  const handleClick = (file) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      if (file.thumbnail && file.thumbnail !== null && file.thumbnail !== "null") {
        setOpenFile(file.id);
      } else {
        FileService.getFile(file.id, true)
          .then((url) => {
            const link = document.createElement("a");
            link.href = url;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch(() => {
            addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
          });
      }
    } else {
      setClickTimeout(
        setTimeout(() => {
          if (selectedFile?.id !== file.id) {
            setSelectedFile(file);
            onFileSelected(file.id);
            setClickTimeout(null);
          }
        }, 250),
      );
    }
  };

  const handleSortKeyChange = (e) => {
    onFileSelected(null);
    setSelectedFile(null);
    setSortConfig((prevConfig) => ({
      ...prevConfig,
      key: e.target.value,
    }));
  };

  const handleSortDirectionChange = (e) => {
    setSelectedFile(null);
    onFileSelected(null);
    setSortConfig((prevConfig) => ({
      ...prevConfig,
      direction: e.target.value,
    }));
  };

  const handleEditMetaData = async (file) => {
    openModal({
      title: i18n.t("Generic.EditFileTitle"),
      hideSaveButton: true,
      form: "formHorse",
      content: (
        <>
          {" "}
          <div style={{ margin: "0 10px" }}>
            <FileUpdateForm ref={metaDataFormRef} id={file.id} />
          </div>
        </>
      ),
      onSave: async () => {
        try {
          if (!metaDataFormRef.current.isValid()) {
            return false;
          }

          const d = metaDataFormRef.current.getData();
          if (d.externalId === null) return;

          await FileService.updateMetaData(d);
          refresh();
          return true;
        } catch (ex) {
          addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
          return false;
        }
      },
      onClose: () => {},
    });
  };

  const handleFileDelete = async (file) => {
    setSelectedFile(file);
    confirmDelete({
      entityId: file.id,
      entityTitle: file.name,
      entityImage: file.thumbnail,
      icon: mediaLibIcon,
      deleteFunction: () => FileService.deleteFile(file.id),
      onClose: () => {
        refresh();
      },
    });
  };

  const sortedFiles = React.useMemo(() => {
    let sortableFiles = [...files];
    if (sortConfig.key !== null) {
      sortableFiles.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableFiles;
  }, [files, sortConfig]);

  const ActionMenu = ({ isVisible, onDelete, onEditMetaData }) => (
    <div className={`${styles.actionMenu} ${isVisible ? styles.visible : ""}`}>
      {canEdit && (
        <MdCreate
          className={styles.actionMenuEdit}
          onClick={(e) => {
            e.stopPropagation();
            onEditMetaData();
          }}
        />
      )}
      <MdDelete
        className={styles.actionMenuDelete}
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
      />
    </div>
  );

  const ListView = () => (
    <div className={styles.listView}>
      {sortedFiles.map((file, index) => (
        <div
          key={index}
          className={`${styles.listRow} ${selectedFile?.id === index ? styles.selected : ""}`}
          onClick={() => {
            setSelectedFile(file);
            onFileSelected(file.id);
          }}>
          <div className={styles.colImgPreview}>
            <div className={styles.imgPreviewContainer}>
              {file.thumbnail ? (
                <img src={file.thumbnail} alt={file.name} className={styles.thumbnail} />
              ) : (
                <MdInsertDriveFile className={styles.fileIcon} />
              )}
            </div>
          </div>
          <div className={styles.colName}>{file.name}</div>
          <div className={styles.colDate}>{file.date}</div>
          <div className={styles.colExtraData}>{file.extraData}</div>
        </div>
      ))}
    </div>
  );

  const GridView = () => (
    <div className={styles.gridView}>
      {sortedFiles.map((file) => (
        <div
          key={file.id}
          className={`${styles.gridItem} ${selectedFile?.id === file.id ? styles.selected : ""}`}
          onMouseEnter={() => setHoveredIndex(file.id)}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={() => {
            handleClick(file);
          }}>
          {canDelete && (
            <ActionMenu
              isVisible={hoveredIndex === file.id}
              onDelete={() => handleFileDelete(file)}
              onEditMetaData={() => handleEditMetaData(file)}
            />
          )}
          <div className={smallIcon ? styles.smallImgPreviewContainer : styles.imgPreviewContainer}>
            {file.thumbnail && file.thumbnail !== "null" ? (
              <img src={file.thumbnail} alt={file.name} className={styles.thumbnail} />
            ) : (
              <MdInsertDriveFile className={styles.fileIcon} />
            )}
          </div>
          <div className={smallIcon ? styles.smallName : styles.name} title={file.name}>
            {file.name}
          </div>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    if (sortedFiles?.length === 0) {
      return (
        <div className={styles.noItemFound}>
          <span>{i18n.t("Generic.List.NoItemFound")}</span>
        </div>
      );
    } else {
      return viewMode === "list" ? <ListView /> : <GridView />;
    }
  };

  return (
    <div className={styles.container}>
      {openFile && (
        <ImageViewer
          id={openFile}
          onClose={() => {
            handleCloseImageViewer();
          }}
        />
      )}
      {!hideHeaderMenu && (
        <div className={styles.sortControls}>
          <label>
            {i18n.t("Generic.SortBy")} :
            <select value={sortConfig.key} onChange={handleSortKeyChange}>
              <option value="name">{i18n.t("Generic.SortBy.Name")}</option>
              <option value="date">{i18n.t("Generic.SortBy.Date")}</option>
            </select>
          </label>
          <label>
            {i18n.t("Generic.SortDirection")} :
            <select value={sortConfig.direction} onChange={handleSortDirectionChange}>
              <option value="ascending">{i18n.t("Generic.SortDirection.asc")}</option>
              <option value="descending">{i18n.t("Generic.SortDirection.dsc")}</option>
            </select>
          </label>
        </div>
      )}
      <div className={styles.content}>{renderContent()}</div>
    </div>
  );
};

export default FileFolderPreview;
